import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import { InfoPanel } from './InfoPanel';
import { Images } from './Images';
import { CatalogProduct } from '../types';
import { Color } from '../../product/types';
import { getIndexName, analyticsEvent } from '../../search/utils';
import { AnalyticsEventTypes, AnalyticsEvents } from '../../search/types';
import { setLastPositionId, setAlgoliaQuery } from '../actions';
import { closeModal, openModalNew } from '../../common/actions';
import { QuickAddMobile } from '../../quickadd/components/QuickAddMobile';
import { QuickAdd } from '../../quickadd/components/QuickAdd';
import { useMediaQueries } from '../../common/hooks/useMediaQuery';
import { pushToGTM, getTrackingProduct } from '../../tracking';
import { Events } from '../../tracking/types';
import { RecommendationEventTypes } from '../../recommendations/types';
import { CmsImage } from '../../cms/types';
import { RootState } from 'src/shared/store/rootReducer';
import paths from '../../routing/paths';

type Props = {
  hit: CatalogProduct;
  indexName?: string;
  recommendationType?: RecommendationEventTypes;
  isFeedback?: boolean;
  isDesktopFeedback?: boolean;
  width?: string;
  alternativeSource?: CmsImage;
  reverseToolTip?: boolean;
  leftAlignToolTip?: boolean;
  numberOfColorsShown: number;
};

const StyledProductCard = styled.div<{ width: string }>`
  width: ${(props) => props.width};
  scroll-margin: 150px;
`;

export const ProductCard = ({
  hit,
  indexName,
  recommendationType,
  isFeedback,
  isDesktopFeedback,
  width = 'initial',
  alternativeSource,
  reverseToolTip = false,
  leftAlignToolTip = false,
  numberOfColorsShown,
}: Props) => {
  const {
    productRef,
    colorRef,
    productName,
    colorLabel,
    detailedColorLabel,
    realColorRef,
    originalPrice,
    storePrice,
    colorVariants,
    promotionPercentage,
    promotionLabel,
    promoBColor,
    promoTColor,
    isOutlet,
    objectID,
    typology,
    collectionName,
    label1, // rank label
    label2: attributeLabel,
    __position: position,
    __queryID: queryID,
  } = hit ?? {};

  const [selectedColor, setSelectedColor] = useState<Color>({
    colorRef,
    colorLabel,
    detailedColorLabel,
    realColorRef,
    originalPrice,
    storePrice,
    promotionPercentage,
    promotionLabel,
    promoBColor,
    promoTColor,
    isOutlet,
    label1,
  });
  const { showFeedback } = useSelector((state: RootState) => state.cart);
  const [firstColorRef] = useState(selectedColor.colorRef);
  const [firstSelectedColor] = useState({
    colorRef: firstColorRef,
    originalPrice,
    storePrice,
  });

  const [showMobileQuickAdd, setShowMobileQuickAdd] = useState(false);
  const handleColorClick = (color: Color) => {
    setSelectedColor(color);
  };

  const dispatch = useDispatch();

  const { search, pathname } = useLocation();

  const { isMobile } = useMediaQueries();

  const elementID = `product-card-${objectID}`;

  const index = indexName || getIndexName(pathname, search);

  const onLinkClick = () => {
    if (recommendationType) {
      pushToGTM(Events.clickedRecommended, {
        product: getTrackingProduct({ product: hit }),
        type: recommendationType,
      });
    }
    analyticsEvent({
      queryID,
      objectID,
      position,
      index,
      type: AnalyticsEventTypes.click,
      eventName: AnalyticsEvents.clickItem,
    });
    dispatch(setLastPositionId(elementID));
    dispatch(setAlgoliaQuery({ queryID, index }));
  };

  const onQuickAddClick = () => {
    analyticsEvent({
      index,
      queryID,
      objectID,
      position,
      type: AnalyticsEventTypes.click,
      eventName: AnalyticsEvents.clickQuickAdd,
    });

    dispatch(setAlgoliaQuery({ queryID, index }));

    const product = getTrackingProduct({ product: hit });

    if (recommendationType) {
      pushToGTM(Events.clickedRecommended, {
        product,
        type: recommendationType,
      });
    }

    pushToGTM(Events.quickAddOpen, {
      product,
    });

    if (isMobile) {
      setShowMobileQuickAdd(true);
    } else {
      dispatch(closeModal());
      dispatch(
        openModalNew({
          content: (
            <QuickAdd
              productRef={productRef}
              colorRef={selectedColor.colorRef}
              originalSelectedColor={colorRef}
            />
          ),
          preset: 'medium',
        })
      );
    }
  };

  useEffect(() => {
    if (
      showFeedback &&
      recommendationType === RecommendationEventTypes.upSell &&
      pathname.startsWith(paths.PRODUCT) &&
      isMobile
    ) {
      setShowMobileQuickAdd(false);
    }
  }, [showFeedback]);

  return !hit ? null : (
    <>
      <StyledProductCard id={`catalog-${elementID}`} width={width}>
        <Images
          productName={productName}
          productRef={productRef}
          objectID={objectID}
          selectedColor={selectedColor}
          onLinkClick={onLinkClick}
          onQuickAddClick={onQuickAddClick}
          isFeedback={isFeedback}
          isDesktopFeedback={isDesktopFeedback}
          alternativeSource={
            firstColorRef === selectedColor.colorRef ? alternativeSource : undefined
          }
        />
        <InfoPanel
          colorVariants={colorVariants}
          productRef={productRef}
          productName={productName}
          objectID={objectID}
          selectedColor={selectedColor}
          firstSelectedColor={firstSelectedColor}
          handleColorClick={handleColorClick}
          typology={typology}
          collectionName={collectionName}
          isFeedback={isFeedback}
          onLinkClick={onLinkClick}
          isDesktopFeedback={isDesktopFeedback}
          reverseToolTip={reverseToolTip}
          leftAlignToolTip={leftAlignToolTip}
          attributeLabel={attributeLabel}
          numberOfColorsShown={numberOfColorsShown}
        />
      </StyledProductCard>
      {!isFeedback && (
        <QuickAddMobile
          productRef={productRef}
          originalSelectedColor={colorRef}
          colorRef={selectedColor.colorRef}
          isActive={showMobileQuickAdd}
          onClose={() => setShowMobileQuickAdd(false)}
        />
      )}
    </>
  );
};
